import {
  AlmanacUpdate,
  CategorizationMap,
  DEFAULT_CREATE_CATEGORY_ROLE,
  FIXED_CATEGORY_DEFAULT_ROLES,
  groupCategoryOptionsByRole,
  hasVisibleShootTime,
  needsFixedCategoryConversion,
  orderCategoriesByRole,
  PLANT_PROFILE_ROLES,
  REQUIRED_PLANT_PROFILE_ROLES,
  resolveOriginCategory,
} from "portal/utils/categoryCollection/category";
import { asOneOf, useQuery } from "portal/utils/hooks/useQuery";
import {
  Button,
  IconButton,
  Paper,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { CategorizedBar } from "../categorized/CategorizedBar";
import { CategorizedImages } from "../categorized/CategorizedImages";
import {
  Category,
  Role,
  roleFromJSON,
} from "protos/robot_syncer/plant_profile";
import { classes } from "portal/theme/theme";
import { ConfirmationDialog } from "portal/components/ConfirmationDialog";
import { ConvertFixedCategoriesDialog } from "../convertCategories/ConvertFixedCategoriesDialog";
import { CreateCategoryDialog } from "../createCategory/CreateCategoryDialog";
import { EditableTypography } from "portal/components/EditableTypography";
import { EditCategoryDialog } from "../editCategory/EditCategoryDialog";
import {
  FeatureFlag,
  useFeatureFlagForCustomer,
} from "portal/utils/hooks/useFeatureFlag";
import { FilterBar } from "../../filters/FilterBar";
import {
  INITIAL_PREDICTION_POINT_FILTERS,
  PredictionPointFilterChipSummary,
  PredictionPointFilters,
} from "../filters/PredictionPointFilters";
import { isValidAlmanac } from "portal/utils/shootTime";
import { LOCALSTORAGE_CATEGORY_COLLECTION_FILTER_BAR } from "portal/utils/localStorage";
import { mergeDeep } from "common/utils/objects";
import { PLANT_PROFILE_TAB_PANEL_CLASSES } from "./PlantProfileView";
import { PlantProfileEditTab, PlantProfileTab } from "./tabs";
import { PlantProfileInstructionsDialog } from "../instructions/PlantProfileInstructionsDialog";
import { PlantProfileRoleSectionHeader } from "../PlantProfileRoleSectionHeader";
import { PreviewResultsSession } from "../session/PreviewResultsSession";
import { RadioChipOption, RadioChips } from "../../RadioChips";
import { ScrollableImageCategorizationOptions } from "../ImageCategorizationOptions";
import { SessionStatus } from "protos/veselka/prediction_point";
import { SetPlantProfile } from "../SetPlantProfile";
import { ShootTimeExpandButton } from "../ShootTimeExpandButton";
import { ShootTimeSection } from "portal/components/shootTime/ShootTimeSection";
import { TAB_QUERY } from "portal/utils/routing";
import { TabContext, TabPanel } from "@mui/lab";
import { ThumbnailControls } from "../ThumbnailControls";
import { titleCase } from "portal/utils/strings";
import { UiPredictionPointFilters } from "portal/utils/categoryCollection/predictionPoints";
import { UnsavedPlantProfile } from "portal/utils/categoryCollection/plantProfile";
import { useCarbonDefaultAlmanac } from "portal/components/shootTime/useCarbonDefaultAlmanac";
import { usePreviewResults } from "../usePreviewResults";
import { useProtectedThumbnailSize } from "../../images/imageSizing/useProtectedThumbnailSize";
import { useShootTimeExpanded } from "../useShootTimeExpanded";
import { useTranslation } from "react-i18next";
import { useUserPreferences } from "portal/state/store";
import AddIcon from "@mui/icons-material/AddOutlined";
import EditIcon from "@mui/icons-material/ModeEditOutlined";
import React, { FC, ReactNode, useCallback, useMemo, useState } from "react";
import SortIcon from "@mui/icons-material/Sort";

const PREVIEW_RESULTS_QUERYABLE_AMOUNT = 500; // amount of chips processed that we feel good about showing the customer

const isSessionReady = (status?: SessionStatus): boolean => {
  return Boolean(
    status &&
      status.countOfResults >=
        Math.min(status.expectedCount, PREVIEW_RESULTS_QUERYABLE_AMOUNT)
  );
};

type PlantProfileEditProps = {
  unsavedPlantProfile: UnsavedPlantProfile;
  setUnsavedPlantProfile: React.Dispatch<
    React.SetStateAction<UnsavedPlantProfile | undefined>
  >;
  activeCategoryId?: string;
  setActiveCategoryId: (categoryId?: string) => void;
  categoryOptions: RadioChipOption[];
  categorizationMap: CategorizationMap;
  createCategoryRadioChipLabel: (option: RadioChipOption) => string;
  isPlantProfileLoading: boolean;
  onImageToggle: (predictionPointId?: string, categoryId?: string) => void;
  isCategorizedDirty: boolean;
  onAlmanacChange: (categoryId: string, update: AlmanacUpdate) => void;
  onAlmanacReset: (categoryId: string) => void;
  onAlmanacValidityChange: (
    categoryId: string,
    fieldId: string,
    isValid: boolean
  ) => void;
  isAlmanacResettable: (categoryId: string) => boolean;
  floatingSaveAction?: ReactNode;
} & (
  | {
      isAdmin: true;
      customerUuid?: never;
      serial?: never;
    }
  | {
      isAdmin?: false;
      customerUuid: string;
      serial?: string;
    }
);

export const PlantProfileEdit: FC<PlantProfileEditProps> = ({
  isAdmin = false,
  customerUuid,
  serial,
  unsavedPlantProfile,
  setUnsavedPlantProfile,
  activeCategoryId,
  setActiveCategoryId,
  categoryOptions,
  categorizationMap,
  createCategoryRadioChipLabel,
  isPlantProfileLoading,
  onImageToggle,
  isCategorizedDirty,
  onAlmanacChange,
  onAlmanacReset,
  onAlmanacValidityChange,
  isAlmanacResettable,
  floatingSaveAction,
}) => {
  const { t } = useTranslation();
  const { almanac: carbonDefaultAlmanac } = useCarbonDefaultAlmanac();
  const { categoryCollection } = useUserPreferences();
  const { isEnabled: hasCustomCategories } = useFeatureFlagForCustomer(
    FeatureFlag.CUSTOM_CATEGORIES,
    customerUuid
  );
  const [isCreateCategoryOpen, setCreateCategoryOpen] = useState(false);
  const [createCategoryDefaultRole, setCreateCategoryDefaultRole] = useState<
    Role | undefined
  >();
  const [isConvertCategoriesOpen, setConvertCategoriesOpen] = useState(false);
  const [editCategoryId, setEditCategoryId] = useState<string | undefined>();
  const [deleteCategoryId, setDeleteCategoryId] = useState<
    string | undefined
  >();
  const needsConversion = needsFixedCategoryConversion(unsavedPlantProfile);

  const editCategory = useMemo(() => {
    if (!editCategoryId) {
      return;
    }
    return unsavedPlantProfile.categories.find(
      ({ id }) => id === editCategoryId
    );
  }, [editCategoryId, unsavedPlantProfile.categories]);

  const editCategoryRole = useMemo(() => {
    if (!editCategory) {
      return roleFromJSON(DEFAULT_CREATE_CATEGORY_ROLE);
    }
    return (
      unsavedPlantProfile.categoryRoles[editCategory.id] ??
      FIXED_CATEGORY_DEFAULT_ROLES[editCategory.name] ??
      roleFromJSON(DEFAULT_CREATE_CATEGORY_ROLE)
    );
  }, [editCategory, unsavedPlantProfile.categoryRoles]);

  const {
    thumbnailDivRef,
    measuredThumbnailMaxSize,
    thumbnailSize,
    dimensions,
  } = useProtectedThumbnailSize(categoryCollection.thumbnailSize);
  const [tabParam, setTab] = useQuery(
    TAB_QUERY,
    asOneOf(PlantProfileTab.ALL, PlantProfileTab.CATEGORIZED)
  );
  const tab = tabParam ?? PlantProfileTab.ALL;
  const [filters, setFilters] = useState<UiPredictionPointFilters>(
    INITIAL_PREDICTION_POINT_FILTERS
  );
  const [modelOverrideId, setModelOverrideId] = useState<string>("");

  const activeCategory = useMemo(
    () => categoryOptions.find(({ id }) => id === activeCategoryId),
    [activeCategoryId, categoryOptions]
  );

  const categoriesByRole = useMemo(
    () =>
      groupCategoryOptionsByRole(
        categoryOptions,
        unsavedPlantProfile.categories,
        unsavedPlantProfile.categoryRoles
      ),
    [
      categoryOptions,
      unsavedPlantProfile.categories,
      unsavedPlantProfile.categoryRoles,
    ]
  );

  const {
    isShootTimeExpanded,
    allShootTimeExpanded,
    setAllShootTimeExpanded,
    onShootTimeExpandedChange,
  } = useShootTimeExpanded(
    unsavedPlantProfile.categories.map((category) => category.id)
  );

  const hasAnyAlmanac = useMemo(
    () =>
      unsavedPlantProfile.categories.some((category) =>
        isValidAlmanac(category.almanac)
      ),
    [unsavedPlantProfile.categories]
  );

  const onAddCategory = useCallback(
    (category: Category, role: Role) => {
      setUnsavedPlantProfile((prev) =>
        prev
          ? {
              ...prev,
              categories: [...prev.categories, category],
              categoryRoles: {
                ...prev.categoryRoles,
                [category.id]: role,
              },
            }
          : prev
      );
      setActiveCategoryId(category.id);
    },
    [setActiveCategoryId, setUnsavedPlantProfile]
  );

  const onConvertFixedCategories = useCallback(
    (categories: Category[], categoryRoles: Record<string, Role>) => {
      setUnsavedPlantProfile((prev) =>
        prev
          ? {
              ...prev,
              categories,
              categoryRoles,
            }
          : prev
      );
      setActiveCategoryId(categories[0]?.id);
    },
    [setActiveCategoryId, setUnsavedPlantProfile]
  );

  const onEditCategory = useCallback(
    (categoryId: string, name: string, role: Role) => {
      setUnsavedPlantProfile((prev) =>
        prev
          ? {
              ...prev,
              categories: prev.categories.map((category) =>
                category.id === categoryId ? { ...category, name } : category
              ),
              categoryRoles: {
                ...prev.categoryRoles,
                [categoryId]: role,
              },
            }
          : prev
      );
    },
    [setUnsavedPlantProfile]
  );

  const onRequestCustomCategoryAction = useCallback(
    (defaultRole?: Role) => {
      if (needsConversion) {
        setConvertCategoriesOpen(true);
        return;
      }
      setCreateCategoryDefaultRole(defaultRole);
      setCreateCategoryOpen(true);
    },
    [needsConversion]
  );

  const onRequestEditCategory = useCallback(
    (categoryId: string) => {
      if (needsConversion) {
        setConvertCategoriesOpen(true);
        return;
      }
      setEditCategoryId(categoryId);
    },
    [needsConversion]
  );

  const onRequestDeleteCategory = useCallback(
    (categoryId: string) => {
      if (needsConversion) {
        setConvertCategoriesOpen(true);
        return;
      }
      setDeleteCategoryId(categoryId);
    },
    [needsConversion]
  );

  const onDeleteCategory = useCallback(
    (categoryId: string) => {
      const remainingCategory = unsavedPlantProfile.categories.find(
        ({ id }) => id !== categoryId
      );
      setUnsavedPlantProfile((prev) => {
        if (!prev) {
          return prev;
        }
        const { [categoryId]: _removed, ...categoryRoles } = prev.categoryRoles;
        return {
          ...prev,
          categories: prev.categories.filter(({ id }) => id !== categoryId),
          categoryRoles,
        };
      });
      if (activeCategoryId === categoryId) {
        setActiveCategoryId(remainingCategory?.id);
      }
    },
    [
      activeCategoryId,
      setActiveCategoryId,
      setUnsavedPlantProfile,
      unsavedPlantProfile.categories,
    ]
  );

  // session
  const onSessionStatusChange = useCallback(
    (sessionId: string, status?: SessionStatus) => {
      if (isSessionReady(status) && filters.sessionId !== sessionId) {
        setFilters((prev) => ({ ...prev, sessionId }));
      }
    },
    [filters.sessionId]
  );
  const {
    previewResults,
    reset: resetPreviewResults,
    canStartSession,
    isLoading: isPreviewResultsLoading,
    session,
  } = usePreviewResults(
    unsavedPlantProfile,
    customerUuid,
    isAdmin || Boolean(customerUuid),
    onSessionStatusChange
  );
  const [areFilterResultsIncomplete, setAreFilterResultsIncomplete] =
    useState(false); // whether the query was attempted before all session results were available
  const setAreResultsIncomplete = useCallback(() => {
    setAreFilterResultsIncomplete(
      Boolean(
        session?.status &&
          session.status.countOfResults < session.status.expectedCount
      )
    );
  }, [session?.status]);
  const resetSession = useCallback(() => {
    resetPreviewResults();
    setFilters((prev) => ({
      ...prev,
      sessionId: undefined,
      categoryIds: [],
    }));
    setAreFilterResultsIncomplete(false);
  }, [resetPreviewResults]);
  const sessionInfo = useMemo(
    () =>
      session
        ? {
            processing: Boolean(
              session.id
                ? !isSessionReady(session.status)
                : isPreviewResultsLoading
            ),
            stale: Boolean(session.stale),
            status: session.status,
          }
        : undefined,
    [isPreviewResultsLoading, session]
  );

  return (
    <>
      <TabContext value={tab}>
        <div className="flex flex-col md:flex-row justify-center md:justify-between items-start md:items-end">
          <div className="flex items-center gap-6">
            <Tabs
              value={tab}
              onChange={(
                _: React.SyntheticEvent,
                newValue: PlantProfileEditTab
              ) => {
                setTab(newValue);
              }}
              slotProps={{
                indicator: {
                  className: tab === PlantProfileTab.ALL ? "ml-1" : undefined,
                  style: {
                    transform: "scaleX(0.75)",
                  },
                },
              }}
            >
              <Tab
                label={t(
                  "components.categoryCollectionProfile.images.allImages"
                )}
                className="text-inherit"
                value={PlantProfileTab.ALL}
              />
              <Tab
                label={
                  <TabLabel
                    label={t(
                      "components.categoryCollectionProfile.images.categorized"
                    )}
                    highlight={isCategorizedDirty}
                  />
                }
                className="text-inherit"
                value={PlantProfileTab.CATEGORIZED}
              />
            </Tabs>
            <PlantProfileInstructionsDialog />
          </div>
          <ThumbnailControls
            measuredThumbnailMaxSize={measuredThumbnailMaxSize}
            thumbnailSize={thumbnailSize}
          />
        </div>
        {/* Note: overflow-visible is required for making the options sticky */}
        <TabPanel
          value={PlantProfileTab.ALL}
          className={classes(
            PLANT_PROFILE_TAB_PANEL_CLASSES,
            "overflow-visible"
          )}
        >
          <div className="sticky top-0 bg-inherit z-10">
            <Paper
              variant="section"
              className="flex items-center py-2 rounded-md pl-2"
            >
              <RadioChips
                isLoading={isPlantProfileLoading}
                options={categoryOptions}
                activeId={activeCategoryId}
                setActiveId={setActiveCategoryId}
                createLabel={createCategoryRadioChipLabel}
              />
              <div className="flex items-center pl-2">
                {hasCustomCategories && (
                  <IconButton
                    onClick={() => onRequestCustomCategoryAction()}
                    aria-label={t(
                      "components.categoryCollectionProfile.actions.createCategory"
                    )}
                    sx={{
                      color: "text.secondary",
                      "&:hover": { color: "text.primary" },
                    }}
                  >
                    <AddIcon />
                  </IconButton>
                )}
              </div>
            </Paper>
          </div>
          {activeCategory ? (
            <div className="pb-4 mt-1">
              <CategorizedBar
                categoryId={activeCategoryId}
                suggestedMin={
                  categoryOptions[0]?.id === activeCategoryId ? 2 : undefined
                }
                categoryOptions={categoryOptions}
                predictionPointIds={
                  unsavedPlantProfile.categories.find(
                    ({ id }) => id === activeCategoryId
                  )?.chipIds ?? []
                }
                dimensions={dimensions}
                onRemove={(predictionPointId) => {
                  onImageToggle(
                    predictionPointId,
                    categorizationMap[predictionPointId]
                  );
                }}
                onClassify={onImageToggle}
                categorizationMap={categorizationMap}
                session={sessionInfo}
              />
            </div>
          ) : undefined}
          <div className="flex flex-col gap-2">
            <FilterBar
              localStorageKey={LOCALSTORAGE_CATEGORY_COLLECTION_FILTER_BAR}
              collapsedSummary={
                <PredictionPointFilterChipSummary filters={filters} />
              }
            >
              <PredictionPointFilters
                filters={filters}
                onChange={(f) => {
                  setFilters(f);
                  if (session?.id) {
                    previewResults(f, modelOverrideId);
                  }
                }}
                disabled={isPreviewResultsLoading && !session?.id}
                hasSession={isPreviewResultsLoading || Boolean(session?.id)}
                scopeCustomerUuid={customerUuid}
                serial={serial}
              />
            </FilterBar>
            <PreviewResultsSession
              onStart={() => {
                previewResults(filters, modelOverrideId);
              }}
              onCancel={() => {
                resetSession();
              }}
              sessionId={
                session?.status?.countOfResults ? session.id : undefined
              }
              modelOverrideId={modelOverrideId}
              setModelOverrideId={(id) => {
                setModelOverrideId(id);
                resetSession();
              }}
              loading={isPreviewResultsLoading}
              disabled={!canStartSession}
              progress={
                session?.status
                  ? {
                      processed: session.status.countOfResults,
                      total: session.status.expectedCount,
                      error: session.error,
                    }
                  : undefined
              }
              sessionFilters={filters}
              onSessionFiltersChange={(sessionFilters) => {
                setFilters((prev) => mergeDeep(prev, sessionFilters));
                setAreResultsIncomplete();
              }}
              categoryOptions={categoryOptions}
            />
          </div>
          <div className="flex justify-end flex-wrap gap-6 mt-2">
            <div className="my-1 lg:my-2 flex gap-2 items-center justify-start">
              <SortIcon className="text-sm" />
              <Typography
                className="text-sm whitespace-nowrap"
                color="textSecondary"
              >
                {t("components.categoryCollectionProfile.images.sortedBy", {
                  sortBy: t(
                    "components.categoryCollectionProfile.images.sortBy.latest"
                  ),
                })}
              </Typography>
            </div>
          </div>
          <div ref={thumbnailDivRef}>
            <ScrollableImageCategorizationOptions
              scopeCustomerUuid={customerUuid}
              filters={{
                ...filters,
                sessionCropIds: session?.filters?.cropIds,
              }}
              areFilterResultsIncomplete={areFilterResultsIncomplete}
              dimensions={dimensions}
              categoryOptions={categoryOptions}
              categorizationMap={categorizationMap}
              session={sessionInfo}
              onImageClick={(predictionPointId) => {
                onImageToggle(predictionPointId, activeCategoryId);
              }}
              onClassify={onImageToggle}
              onRefreshOptions={setAreResultsIncomplete}
              floatingAction={floatingSaveAction}
            />
          </div>
        </TabPanel>
        <TabPanel
          value={PlantProfileTab.CATEGORIZED}
          className={classes(
            PLANT_PROFILE_TAB_PANEL_CLASSES,
            "overflow-visible"
          )}
        >
          <div className="flex flex-col gap-4">
            {hasAnyAlmanac && (
              <Paper
                variant="section"
                elevation={0}
                className="sticky top-0 z-10 py-2 flex flex-wrap items-center justify-center gap-4 rounded-none"
              >
                <ShootTimeExpandButton
                  expanded={allShootTimeExpanded}
                  onToggle={() =>
                    setAllShootTimeExpanded(!allShootTimeExpanded)
                  }
                />
              </Paper>
            )}
            {PLANT_PROFILE_ROLES.filter((role) => {
              const categories = categoriesByRole.get(role) ?? [];
              // only show disregard section if there's a category assigned to that role
              return role !== Role.DISREGARD || categories.length > 0;
            }).map((role) => {
              const categories = categoriesByRole.get(role) ?? [];
              const isEmpty = categories.length === 0;
              const isRequiredEmptyRole =
                (REQUIRED_PLANT_PROFILE_ROLES as readonly Role[]).includes(
                  role
                ) && isEmpty;
              const renderCreateCategoryButton = (): ReactNode | undefined =>
                hasCustomCategories && role !== Role.DISREGARD ? (
                  <Button
                    color="secondary"
                    variant="contained"
                    startIcon={<AddIcon />}
                    className="leading-[0]"
                    onClick={() => onRequestCustomCategoryAction(role)}
                  >
                    {t(
                      "components.categoryCollectionProfile.actions.createCategory"
                    )}
                  </Button>
                ) : undefined;
              return (
                <div key={role} className="flex flex-col gap-2">
                  <PlantProfileRoleSectionHeader
                    role={role}
                    action={renderCreateCategoryButton()}
                  />
                  {isRequiredEmptyRole && (
                    <div className="flex flex-col items-center justify-center gap-3 rounded-md border-2 border-dashed border-carbon-gray-light px-4 py-8 text-center">
                      <Typography color="textSecondary">
                        {t(
                          role === Role.CROP
                            ? "components.categoryCollectionProfile.warnings.emptyCrop"
                            : "components.categoryCollectionProfile.warnings.emptyWeed"
                        )}
                      </Typography>
                      {renderCreateCategoryButton()}
                    </div>
                  )}
                  {isEmpty && !isRequiredEmptyRole && (
                    <div className="flex justify-center py-2 text-center italic">
                      <Typography color="textSecondary">
                        {t("utils.descriptors.none")}
                      </Typography>
                    </div>
                  )}
                  {!isEmpty &&
                    categories.map((categoryOption) => {
                      const category = unsavedPlantProfile.categories.find(
                        ({ id }) => id === categoryOption.id
                      );
                      const shootTime =
                        hasVisibleShootTime(role) &&
                        isValidAlmanac(category?.almanac) ? (
                          <ShootTimeSection
                            almanac={category.almanac}
                            carbonDefaultAlmanac={carbonDefaultAlmanac}
                            originCategory={resolveOriginCategory(
                              category.almanac.originCategoryId
                            )}
                            compact
                            expanded={isShootTimeExpanded(categoryOption.id)}
                            onExpandedChange={(expanded) => {
                              onShootTimeExpandedChange(
                                categoryOption.id,
                                expanded
                              );
                            }}
                            canEdit
                            onChange={(formulas) => {
                              onAlmanacChange(categoryOption.id, { formulas });
                            }}
                            onSizesChange={(sizeBoundariesMm) => {
                              onAlmanacChange(categoryOption.id, {
                                sizeBoundariesMm,
                              });
                            }}
                            onReset={() => {
                              onAlmanacReset(categoryOption.id);
                            }}
                            isResettable={isAlmanacResettable(
                              categoryOption.id
                            )}
                            onFieldValidityChange={(fieldId, isValid) => {
                              onAlmanacValidityChange(
                                categoryOption.id,
                                fieldId,
                                isValid
                              );
                            }}
                            customerUuid={customerUuid}
                            onResetFromAlmanac={(update) => {
                              onAlmanacChange(categoryOption.id, update);
                            }}
                          />
                        ) : undefined;
                      return (
                        <CategorizedImages
                          key={categoryOption.id}
                          categoryId={categoryOption.id}
                          categoryName={category?.name}
                          categoryOptions={categoryOptions}
                          predictionPointIds={category?.chipIds ?? []}
                          dimensions={dimensions}
                          onRemove={(predictionPointId: string) => {
                            onImageToggle(
                              predictionPointId,
                              categorizationMap[predictionPointId]
                            );
                          }}
                          onClassify={onImageToggle}
                          classificationAction="reclassify"
                          rightClickMenuTitle={titleCase(
                            t(
                              "components.categoryCollectionProfile.actions.reclassify"
                            )
                          )}
                          categorizationMap={categorizationMap}
                          session={sessionInfo}
                          onRequestEditCategory={
                            hasCustomCategories
                              ? () => onRequestEditCategory(categoryOption.id)
                              : undefined
                          }
                          onRequestDeleteCategory={
                            hasCustomCategories
                              ? () => onRequestDeleteCategory(categoryOption.id)
                              : undefined
                          }
                        >
                          {shootTime}
                        </CategorizedImages>
                      );
                    })}
                </div>
              );
            })}
          </div>
        </TabPanel>
      </TabContext>
      {hasCustomCategories && (
        <>
          <CreateCategoryDialog
            open={isCreateCategoryOpen}
            onClose={() => {
              setCreateCategoryOpen(false);
              setCreateCategoryDefaultRole(undefined);
            }}
            unsavedPlantProfile={unsavedPlantProfile}
            customerUuid={customerUuid}
            defaultRole={createCategoryDefaultRole}
            onAdd={onAddCategory}
          />
          <ConvertFixedCategoriesDialog
            open={isConvertCategoriesOpen}
            onClose={() => setConvertCategoriesOpen(false)}
            unsavedPlantProfile={unsavedPlantProfile}
            customerUuid={customerUuid}
            onConvert={onConvertFixedCategories}
          />
          {editCategory && (
            <EditCategoryDialog
              open
              onClose={() => setEditCategoryId(undefined)}
              categoryName={editCategory.name}
              role={editCategoryRole}
              onSave={(name, role) =>
                onEditCategory(editCategory.id, name, role)
              }
            />
          )}
          {deleteCategoryId && (
            <ConfirmationDialog
              title={t("utils.actions.removeLong", {
                subject: t(
                  "models.categoryCollectionProfiles.category_one_title"
                ),
              })}
              description={t("components.ConfirmationDialog.title")}
              yesText={t("utils.descriptors.yes")}
              onClose={() => setDeleteCategoryId(undefined)}
              onYes={() => onDeleteCategory(deleteCategoryId)}
            />
          )}
        </>
      )}
    </>
  );
};

// Profile editing for fixed profiles
type MetadataEditButtonProps = {
  unsavedPlantProfile: UnsavedPlantProfile;
  customerUuid?: string;
  setUnsavedPlantProfile: React.Dispatch<
    React.SetStateAction<UnsavedPlantProfile | undefined>
  >;
  setActiveCategoryId: (categoryId?: string) => void;
};

export const MetadataEditButton: FC<MetadataEditButtonProps> = ({
  unsavedPlantProfile,
  customerUuid,
  setUnsavedPlantProfile,
  setActiveCategoryId,
}) => {
  const { t } = useTranslation();
  return (
    <SetPlantProfile
      triggerButton={(onClick) => (
        <IconButton
          onClick={onClick}
          sx={{
            color: "text.secondary",
            "&:hover": { color: "text.primary" },
          }}
        >
          <EditIcon />
        </IconButton>
      )}
      hotkey={{ sequence: "ec", key: "EDIT_PLANT_PROFILE" }}
      title={t("utils.actions.editLong", {
        subject: t(
          "models.categoryCollectionProfiles.categoryCollectionProfile_one"
        ),
      })}
      okText={t("utils.actions.next")}
      initialValue={unsavedPlantProfile}
      customerUuid={customerUuid}
      onSubmit={(profile) => {
        setUnsavedPlantProfile(profile);
        const ordered = orderCategoriesByRole(
          profile.categories,
          profile.categoryRoles
        );
        setActiveCategoryId(ordered[0]?.id ?? profile.categories[0]?.id);
      }}
    />
  );
};

type PlantProfileEditableHeaderProps = {
  unsavedPlantProfile: UnsavedPlantProfile;
  customerUuid?: string;
  setUnsavedPlantProfile: React.Dispatch<
    React.SetStateAction<UnsavedPlantProfile | undefined>
  >;
  setActiveCategoryId: (categoryId?: string) => void;
};

/** Edit-mode title cluster for the plant profile page header. */
export const PlantProfileEditableHeader: FC<
  PlantProfileEditableHeaderProps
> = ({
  unsavedPlantProfile,
  customerUuid,
  setUnsavedPlantProfile,
  setActiveCategoryId,
}) => {
  const { isEnabled: hasCustomCategories } = useFeatureFlagForCustomer(
    FeatureFlag.CUSTOM_CATEGORIES,
    customerUuid
  );

  if (hasCustomCategories) {
    // with custom categories -- you do category edits via the dedicated button not the form
    return (
      <EditableTypography
        variant="h4"
        value={unsavedPlantProfile.name}
        onEdit={(name) =>
          setUnsavedPlantProfile((prev) => (prev ? { ...prev, name } : prev))
        }
      />
    );
  }

  // with fixed -- use the old school form
  return (
    <>
      <Typography variant="h4">{unsavedPlantProfile.name}</Typography>
      <MetadataEditButton
        unsavedPlantProfile={unsavedPlantProfile}
        customerUuid={customerUuid}
        setUnsavedPlantProfile={setUnsavedPlantProfile}
        setActiveCategoryId={setActiveCategoryId}
      />
    </>
  );
};

interface TabLabelProps {
  label: string;
  highlight: boolean;
}

const TabLabel: FC<TabLabelProps> = ({ label, highlight }) => (
  <span className="flex items-center gap-2">
    {label}
    {highlight && (
      <span className="size-2 rounded-full bg-carbon-orange" aria-hidden />
    )}
  </span>
);
